import React, { useState } from "react";
import { Upload, Button, message, Spin, Modal } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import { uploadClientDocuments } from "../../api/client/clientRequest";
import { handleFilename } from "../../utils";
import PermissionRender from "../../components/PermissionRender";

const { Dragger } = Upload;

const DocumentUpload = ({ client, onDocumentsUploaded }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const showModal = () => {
    setFileList([]);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setFileList([]);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning(
        "Veuillez sélectionner au moins un document à télécharger"
      );
      return;
    }

    setUploading(true);

    const formData = new FormData();
    fileList.forEach((file) => {
      // Apply the filename handling before adding to formData
      const processedFile = handleFilename(file);
      formData.append("new_files[]", processedFile);
    });

    try {
      const response = await uploadClientDocuments(client.id, formData);
      message.success("Documents téléchargés avec succès");
      setFileList([]);
      setIsModalVisible(false);

      // Call the callback to update parent component
      if (onDocumentsUploaded) {
        onDocumentsUploaded(response);
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      message.error("Erreur lors du téléchargement des documents");
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      // Check file type
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
      ].includes(file.type);

      // Check file size (10MB)
      const isLessThan10MB = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error(`${file.name} n'est pas un type de fichier supporté`);
        return Upload.LIST_IGNORE;
      }

      if (!isLessThan10MB) {
        message.error(`${file.name} dépasse 10MB`);
        return Upload.LIST_IGNORE;
      }

      // Process the filename before adding to fileList
      const processedFile = handleFilename(file);

      // If we changed the filename, show a notification
      if (file.name !== processedFile.name) {
        message.info(
          `Fichier "${file.name}" renommé en "${processedFile.name}"`
        );
      }

      setFileList([...fileList, processedFile]);
      return false;
    },
    fileList,
  };

  return (
    <>
      <div className="flex justify-end">
        <PermissionRender requiredPermission={["clients", "update"]}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={showModal}
            style={{ borderRadius: "4px" }}
          >
            Joindre des documents
          </Button>
        </PermissionRender>
      </div>

      <Modal
        title="Joindre des documents au client"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploading}
            onClick={handleUpload}
            disabled={fileList.length === 0}
          >
            Envoyer
          </Button>,
        ]}
      >
        <Spin spinning={uploading}>
          <Dragger {...uploadProps} className="mb-4">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Cliquez ou glissez-déposez des fichiers ici pour les importer{" "}
            </p>
            <p className="ant-upload-hint">
              Support pour PDF, Word, Excel, images (JPG, PNG, GIF) et TXT.
              Maximum 10MB par fichier.
            </p>
          </Dragger>

          <div className="mt-4">
            {fileList.length > 0 && (
              <p className="text-gray-600">
                {fileList.length} fichier(s) prêt(s) à être envoyés
              </p>
            )}
          </div>
        </Spin>
      </Modal>
    </>
  );
};

export default DocumentUpload;
